/**
 * Public auth hooks for the admin SPA.
 *
 * - `useAuth()`   — re-export of the context (token + user + helpers).
 * - `useSession()`— TanStack Query for `/auth/me`, enabled only when a token is
 *                   in memory. Reacts to token changes via the context — fixes
 *                   review finding C-2 (the previous version captured the token
 *                   in a module-level singleton and never re-fired).
 * - `useLogin()`  — mutation for `POST /auth/login`, handles `requires_2fa`
 *                   (200-with-a-status union → `ApiError`, see
 *                   `narrowLoginResponse`).
 * - `useLogout()` — mutation for `POST /auth/logout`, always clears local
 *                   session regardless of server result.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth as useAuthContext, type AuthUser } from '@/lib/auth-provider';
import { apiClient, ApiError } from '@/lib/api-client';
import { narrowLoginResponse } from '@/lib/login-response';
import { STALE_TIME } from '@/lib/query-client';

export { useAuth } from '@/lib/auth-provider';
export type { AuthUser } from '@/lib/auth-provider';

const QUERY_KEY_SESSION = ['auth', 'session'] as const;

export interface LoginInputDto {
  email: string;
  password: string;
  totp_code?: string;
  /** "I lost my phone" alternative to `totp_code` (TASK-450). */
  recovery_code?: string;
}

export interface LoginSuccess {
  access_token: string;
  expires_at: string;
  user: AuthUser;
}

export function useSession() {
  const { accessToken } = useAuthContext();
  return useQuery<AuthUser, ApiError>({
    queryKey: QUERY_KEY_SESSION,
    queryFn: () => apiClient<AuthUser>('/auth/me'),
    // Reactive — re-evaluates whenever the in-memory token changes.
    enabled: !!accessToken,
    staleTime: STALE_TIME.single,
  });
}

export function useLogin() {
  const { setAccessToken, setUser } = useAuthContext();
  const queryClient = useQueryClient();
  return useMutation<LoginSuccess, ApiError, LoginInputDto>({
    mutationFn: async (input) =>
      narrowLoginResponse(
        await apiClient<LoginSuccess & { status?: string }>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(input),
          skipSessionExpiredEvent: true,
        }),
      ),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      setUser(data.user);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY_SESSION });
    },
  });
}

export function useLogout() {
  const { clearSession } = useAuthContext();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      apiClient<void>('/auth/logout', {
        method: 'POST',
        skipSessionExpiredEvent: true,
      }),
    onSettled: () => {
      clearSession();
    },
  });
}
